import React from "react";
import { COLORS, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "../theme.js";
import { LogoMark, Wordmark } from "../components/ui.jsx";
import { configIssues } from "../lib/supabase.js";

/* Shown when VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are missing.
   Deliberately blocks the app: without a backend there is no real account to
   sign into, and a screen that pretends otherwise is what this build replaced. */
export default function SetupNotice() {
  const steps = [
    ["Create a Supabase project", "supabase.com → New project. The free tier is enough for beta."],
    ["Run the migration", "SQL Editor → paste supabase/migrations/0001_init.sql → Run."],
    ["Copy your API keys", "Project Settings → API. You need the Project URL and the anon public key."],
    ["Fill in .env.local", "cp .env.example .env.local, paste both values, then restart `npm run dev`."],
  ];

  return (
    <div style={{ height: "100%", overflowY: "auto", padding: "40px 24px", display: "flex", flexDirection: "column", gap: 22 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
        <LogoMark size={56} />
        <Wordmark size={20} />
      </div>

      <div style={{ textAlign: "center" }}>
        <div style={{ fontFamily: FONT_DISPLAY, fontSize: 19, color: COLORS.cream }}>Backend not connected</div>
        <div style={{ fontSize: 12.5, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginTop: 6, lineHeight: 1.5 }}>
          The app is built and ready — it just needs your Supabase credentials before it can hold real accounts and real data.
        </div>
      </div>

      {/* Naming the exact variable turns a deploy mystery into a one-line fix. */}
      {configIssues.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 10.5, fontFamily: FONT_MONO, letterSpacing: 0.8, color: COLORS.creamFaint, textTransform: "uppercase" }}>
            What's wrong
          </div>
          {configIssues.map((issue) => {
            const danger = issue.severity === "danger";
            return (
              <div
                key={issue.key}
                style={{
                  background: danger ? "rgba(180,69,63,0.12)" : COLORS.blackCard,
                  border: `1px solid ${danger ? COLORS.red : COLORS.lineFaint}`,
                  borderRadius: 12,
                  padding: 14,
                }}
              >
                <div style={{ fontFamily: FONT_MONO, fontSize: 11.5, color: danger ? COLORS.red : COLORS.goldBright, wordBreak: "break-all" }}>
                  {danger ? "⚠ " : ""}
                  {issue.key}
                </div>
                <div style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.cream, marginTop: 5, lineHeight: 1.5 }}>{issue.problem}</div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ background: COLORS.blackCard2, border: `1px solid ${COLORS.line}`, borderRadius: 12, padding: 14 }}>
        <div style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.cream, fontWeight: 600 }}>Deployed and still seeing this?</div>
        <div style={{ fontFamily: FONT_BODY, fontSize: 11.5, color: COLORS.creamDim, marginTop: 5, lineHeight: 1.6 }}>
          These values are baked in when the site is <b>built</b>, not read while it runs. Setting them
          on your host does nothing until you start a <b>new build</b> — on Netlify, Deploys → Trigger
          deploy → Clear cache and deploy site.
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {steps.map(([title, body], i) => (
          <div key={title} style={{ background: COLORS.blackCard, border: `1px solid ${COLORS.lineFaint}`, borderRadius: 14, padding: 16, display: "flex", gap: 14 }}>
            <span
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                flex: "none",
                border: `1px solid ${COLORS.gold}`,
                color: COLORS.goldBright,
                fontFamily: FONT_MONO,
                fontSize: 11.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {i + 1}
            </span>
            <div>
              <div style={{ fontFamily: FONT_BODY, fontSize: 14, color: COLORS.cream, fontWeight: 600 }}>{title}</div>
              <div style={{ fontFamily: FONT_MONO, fontSize: 11.5, color: COLORS.creamDim, marginTop: 4, lineHeight: 1.6 }}>{body}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 11, fontFamily: FONT_BODY, color: COLORS.creamFaint, textAlign: "center", lineHeight: 1.6 }}>
        Full walkthrough in <b style={{ color: COLORS.goldBright }}>README.md</b>.
      </div>
    </div>
  );
}
